'use client'

import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { useLocale } from 'next-intl'
import type {
  Product,
  ProductStatusQueryProps,
} from '@skop-app/lib-domain/types'

import { useUserContext } from '@/context'
import { getPersonalWorkspaceMembership } from '@/features/team-selection'
import { searchProductStatuses } from '@/services/queries/product'
import {
  getProductStatusesItems,
  type ProductStatusesQueryKey,
  type ProductStatusesRouteHint,
} from '@/services/product/productStatuses.cache'

/**
 * Fetches inventory products directly via TanStack `useInfiniteQuery`.
 *
 * Returns a flat `Product[]` (all pages merged).
 * Tree data grouping is handled downstream by `normalizeInventoryRows`.
 */
export const useInventoryProducts = ({
  filters,
  isOrganizationView,
  enabled = true,
  pageSize,
  routeHint,
}: ProductStatusQueryProps & {
  enabled?: boolean
  pageSize?: number
  routeHint?: ProductStatusesRouteHint
}) => {
  const locale = useLocale()
  const lang = locale.toUpperCase()
  const { isClient, ownership, organizationScope, organizationMemberships } =
    useUserContext()

  const isMaster = organizationScope.selectedOrganization?.isMaster ?? false
  const personalWorkspaceOrganizationID =
    organizationScope.scopeKind === 'solo' && !isOrganizationView
      ? getPersonalWorkspaceMembership(organizationMemberships)?.value
      : undefined

  const queryKey: ProductStatusesQueryKey = [
    'productStatuses',
    {
      userID: ownership?.byUserID,
      scopeKind: organizationScope.scopeKind,
      personalWorkspaceOrganizationID,
      organizationID: !isOrganizationView
        ? organizationScope.activeOrganizationId
        : organizationScope.activeOrganizationMasterId,
      isOrganizationView,
      isMaster,
      filters,
      lang,
      pageSize,
      routeHint,
    },
  ]
  const queryScope = queryKey[1]
  const canFetchInventory = Boolean(
    queryScope?.userID || queryScope?.organizationID,
  )
  const isQueryEnabled = enabled && isClient && canFetchInventory

  const {
    data,
    hasNextPage,
    fetchNextPage,
    isFetching,
    isError,
    isLoading,
    isFetchingNextPage,
    isFetched,
  } = useInfiniteQuery({
    queryKey,
    queryFn: (props) =>
      searchProductStatuses({
        props,
        filters,
      }),
    enabled: isQueryEnabled,
    initialPageParam: null,
    getNextPageParam: (lastPage) => lastPage.nextToken ?? undefined,
  })

  // Auto-fetch remaining pages until all data is loaded
  useEffect(() => {
    if (
      !isQueryEnabled ||
      !hasNextPage ||
      isFetching ||
      isFetchingNextPage ||
      isError
    )
      return
    void fetchNextPage()
  }, [
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isQueryEnabled,
  ])

  // Flatten all pages into a single Product[]
  const products = useMemo<Product[] | undefined>(() => {
    if (!data) return undefined
    return getProductStatusesItems(data)
  }, [data])

  const isFullyLoaded =
    isFetched && !hasNextPage && !isFetching && !isFetchingNextPage && !isError
  const hasInitialPageLoaded = isFetched && !isError

  return {
    products,
    isLoading,
    isFetching,
    isFetched,
    hasNextPage,
    isFetchingNextPage,
    isError,
    isFullyLoaded,
    hasInitialPageLoaded,
  }
}
